import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  FormHelperText,
} from '@mui/material';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useGetAllBuildings } from '@/api/endpoints/building/useGetAllBuildings';
import { useGetCommunityContact } from '@/api/endpoints/contact/useGetCommunityContact';
import { useGetContact } from '@/api/endpoints/contact/useGetContact';
import { useUpdateCommunityContact } from '@/api/endpoints/contact/useUpdateCommunityContact';
import {
  useUpdateContact,
  ApiRequest_Update_Contact,
} from '@/api/endpoints/contact/useUpdateContact';
import { useGetAllPropertyLocations } from '@/api/endpoints/propertyLocation/useGetAllPropertyLocations';
import { useGetAllUnitsInPropertyLocation } from '@/api/endpoints/unit/useGetAllUnitsInPropertyLocation';
import { ContactDetailType , ContactRole } from '@/api/models/contact';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { getResolvedRoutePath } from '@/utils/router';

import FormAutocomplete, { Option } from '@/components/form/FormAutocomplete';
import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';


interface ContactEditPageParams {
  id: string;
}

interface ContactMetadata {
  additional_details?: string;
  [key: string]: unknown;
}

interface ContactDetailFormData {
  id?: number;
  type: ContactDetailType;
  value: string;
  label?: string;
  is_primary?: boolean;
}

interface ContactFormData
  extends Omit<
    ApiRequest_Update_Contact,
    'details' | 'building_ids' | 'unit_ids' | 'location_ids'
  > {
  role: ContactRole;
  details: ContactDetailFormData[];
  building_ids: Option[];
  unit_ids: Option[];
  location_ids: Option[];
  additional_details?: string;
}

const contactRoleOptions: { label: string; value: ContactRole }[] = [
  { label: 'Tenant', value: 'TENANT' },
  { label: 'Landlord', value: 'LANDLORD' },
  { label: 'Property Manager', value: 'PROPERTY_MANAGER' },
  { label: 'Owner Representative', value: 'OWNER_REPRESENTATIVE' },
  { label: 'Cleaning Service', value: 'CLEANING_SERVICE' },
  { label: 'HVAC Contractor', value: 'HVAC_CONTRACTOR' },
  { label: 'Electric Provider', value: 'ELECTRIC_PROVIDER' },
  { label: 'Water Provider', value: 'WATER_PROVIDER' },
  { label: 'Wastewater Service', value: 'WASTEWATER_SERVICE' },
  { label: 'Propane Supplier', value: 'PROPANE_SUPPLIER' },
  { label: 'Trash Removal', value: 'TRASH_REMOVAL' },
  { label: 'Tree Service', value: 'TREE_SERVICE' },
  { label: 'Pest Control', value: 'PEST_CONTROL' },
  { label: 'Pool Service', value: 'POOL_SERVICE' },
  { label: 'Lawn Care', value: 'LAWN_CARE' },
  { label: 'Landscaping Vendor', value: 'LANDSCAPING_VENDOR' },
  { label: 'Security Provider', value: 'SECURITY_PROVIDER' },
  { label: 'Sound System Vendor', value: 'SOUND_SYSTEM_VENDOR' },
  { label: 'Water Cooler Vendor', value: 'WATER_COOLER_VENDOR' },
  { label: 'Tax Authority', value: 'TAX_AUTHORITY' },
  { label: 'Insurance Agent', value: 'INSURANCE_AGENT' },
  { label: 'Legal Representative', value: 'LEGAL_REPRESENTATIVE' },
  { label: 'Vendor', value: 'VENDOR' },
  { label: 'Contractor', value: 'CONTRACTOR' },
  { label: 'Service Provider', value: 'SERVICE_PROVIDER' },
  { label: 'Emergency Contact', value: 'EMERGENCY_CONTACT' },
  { label: 'Government', value: 'GOVERNMENT' },
  { label: 'Utility', value: 'UTILITY' },
  { label: 'Other', value: 'OTHER' },
];

const contactDetailTypeOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
];

const contactDetailLabelOptions = [
  { label: 'Primary', value: 'primary' },
  { label: 'Work', value: 'work' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Home', value: 'home' },
  { label: 'Other', value: 'other' },
];

const ContactEditPage: React.FC = () => {
  const { id } = useParams<ContactEditPageParams>();
  const { selectedPropertyLocation } = usePropertyManager();
  const history = useHistory();
  const location = useLocation();
  const [formError, setFormError] = useState<Error | null>(null);

  // Check if we're editing a community contact
  const urlParams = new URLSearchParams(location.search);
  const communityId = urlParams.get('community_id');
  const isCommunityContact = !!communityId;

  const contactId = parseInt(id, 10);

  // Fetch contact data using appropriate hook based on context
  const getLocationContactQuery = useGetContact(
    contactId, 
    !isCommunityContact && !!selectedPropertyLocation
  );
  
  const getCommunityContactQuery = useGetCommunityContact(
    contactId,
    communityId ? parseInt(communityId, 10) : 0,
    isCommunityContact && !!communityId
  );
  
  // Use the appropriate query based on context
  const contactQuery = isCommunityContact ? getCommunityContactQuery : getLocationContactQuery;
  const contact = contactQuery.data;
  const isLoadingContact = contactQuery.isLoading;
  const contactError = contactQuery.error;

  // Fetch all property locations for the property manager
  const { data: propertyLocations } = useGetAllPropertyLocations();

  // Fetch buildings for the current location
  const { data: buildings, isLoading: isLoadingBuildings } = useGetAllBuildings(
    { location_id: selectedPropertyLocation?.id },
    !!selectedPropertyLocation?.id
  );

  // Fetch units for the current location
  const { data: units, isLoading: isLoadingUnits } = useGetAllUnitsInPropertyLocation(
    { propertyLocationId: selectedPropertyLocation?.id },
    !!selectedPropertyLocation?.id
  );

  // Create options for property locations
  const propertyLocationOptions: Option[] = useMemo(
    () =>
      propertyLocations?.map(propLoc => ({
        label: propLoc.name,
        value: propLoc.id,
      })) || [],
    [propertyLocations]
  );

  // Create options for buildings and units
  const buildingOptions: Option[] = useMemo(
    () =>
      buildings?.map(building => ({
        label: building.name || building.address_text,
        value: building.id,
      })) || [],
    [buildings]
  );

  const unitOptions: Option[] = useMemo(
    () =>
      units?.map(unit => ({
        label: unit.name,
        value: unit.id,
      })) || [],
    [units]
  );

  const form = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      role: 'TENANT' as ContactRole,
      details: [],
      building_ids: [],
      unit_ids: [],
      location_ids: [],
      additional_details: '',
    },
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const {
    fields: detailFields,
    append: appendDetail,
    remove: removeDetail,
  } = useFieldArray({
    control,
    name: 'details',
  });

  // Watch form values for validation and conditional display
  const watchedRole = watch('role');

  // Check if role is tenant to show building/unit fields
  const isTenantRole = watchedRole === 'TENANT';

  // Update form when contact data is loaded
  useEffect(() => {
    if (contact && propertyLocationOptions.length > 0) {
      const metadata = contact.metadata as ContactMetadata | null;

      // Get selected property location options from contact associations
      const selectedLocationIds =
        contact.associations
          ?.filter(assoc => assoc.location_id)
          ?.map(assoc => assoc.location_id)
          .filter((locationId): locationId is number => locationId !== null) || [];

      const selectedLocationOptions = propertyLocationOptions.filter(option =>
        selectedLocationIds.includes(Number(option.value))
      );

      // Get selected building and unit options from contact associations
      const selectedBuildingIds =
        contact.associations
          ?.filter(assoc => assoc.building_id)
          ?.map(assoc => assoc.building_id)
          .filter((buildingId): buildingId is number => buildingId !== null) || [];

      const selectedUnitIds =
        contact.associations
          ?.filter(assoc => assoc.unit_id)
          ?.map(assoc => assoc.unit_id)
          .filter((unitId): unitId is number => unitId !== null) || [];

      const selectedBuildingOptions = buildingOptions.filter(option =>
        selectedBuildingIds.includes(Number(option.value))
      );

      const selectedUnitOptions = unitOptions.filter(option =>
        selectedUnitIds.includes(Number(option.value))
      );

      reset({
        name: contact.name,
        role: contact.role as ContactRole,
        user_id: contact.user_id,
        metadata: contact.metadata,
        additional_details: metadata?.additional_details || '',
        details:
          contact.details?.map(detail => ({
            id: detail.id,
            type: detail.type as ContactDetailType,
            value: detail.value,
            label: detail.label || '',
            is_primary: detail.is_primary,
          })) || [],
        building_ids: selectedBuildingOptions,
        unit_ids: selectedUnitOptions,
        location_ids: selectedLocationOptions,
      });
    }
  }, [contact, reset, buildingOptions, unitOptions, propertyLocationOptions]);

  const updateLocationContactMutation = useUpdateContact(contactId, {
    onSuccess: () => {
      const viewPath = getResolvedRoutePath(PMPRoutes.contactView, { id });
      history.push(viewPath);
    },
    onError: error => {
      setFormError(error);
    },
  });

  const updateCommunityContactMutation = useUpdateCommunityContact(
    contactId,
    communityId ? parseInt(communityId, 10) : 0,
    {
      onSuccess: () => {
        history.push(getResolvedRoutePath(PMPRoutes.contactView, { id }));
      },
      onError: error => {
        setFormError(error);
      },
    });

  const handleBack = () => {
    const viewPath = getResolvedRoutePath(PMPRoutes.contactView, { id });
    if (isCommunityContact && communityId) {
      history.push(`${viewPath}?community_id=${communityId}`);
    } else {
      history.push(viewPath);
    }
  };

  const handleCancel = () => {
    const viewPath = getResolvedRoutePath(PMPRoutes.contactView, { id });
    if (isCommunityContact && communityId) {
      history.push(`${viewPath}?community_id=${communityId}`);
    } else {
      history.push(viewPath);
    }
  };

  const onSubmit = useCallback(
    (data: ContactFormData) => {
      setFormError(null);

      // Filter out empty contact details
      const filteredDetails = data.details.filter(detail => detail.value.trim() !== '');

      // Prepare metadata with additional details
      const existingMetadata = (contact?.metadata as ContactMetadata) || {};
      const metadata: ContactMetadata = {
        ...existingMetadata,
      };

      if (data.additional_details?.trim()) {
        metadata.additional_details = data.additional_details.trim();
      } else {
        delete metadata.additional_details;
      }

      if (isCommunityContact) {
        // For community contacts, we don't need location validation
        return updateCommunityContactMutation.mutateAsync({
          name: data.name || '',
          role: data.role,
          user_id: data.user_id || undefined,
          metadata,
          details: filteredDetails.length > 0 ? filteredDetails : undefined,
        }).then(() => {
          const viewPath = getResolvedRoutePath(PMPRoutes.contactView, { id });
          history.push(`${viewPath}?community_id=${communityId}`);
        });
      }

      // Validate at least one location is selected for location contacts
      if (!data.location_ids || data.location_ids.length === 0) {
        setFormError(new Error('At least one property location must be selected'));
        return;
      }

      // Convert Option arrays back to number arrays
      const locationIds = data.location_ids.map(option => Number(option.value));
      const buildingIds = data.building_ids.map(option => Number(option.value));
      const unitIds = data.unit_ids.map(option => Number(option.value));

      return updateLocationContactMutation.mutateAsync({
        name: data.name,
        role: data.role,
        user_id: data.user_id,
        metadata,
        details: filteredDetails.length > 0 ? filteredDetails : undefined,
        location_ids: locationIds,
        building_ids: buildingIds.length > 0 ? buildingIds : undefined,
        unit_ids: unitIds.length > 0 ? unitIds : undefined,
      });
    },
    [
      updateLocationContactMutation, 
      updateCommunityContactMutation, 
      contact?.metadata, 
      isCommunityContact, 
      communityId, 
      history, 
      id
    ]
  );

  const handleAddDetail = () => {
    appendDetail({ type: 'email' as ContactDetailType, value: '', is_primary: false });
  };

  if (isLoadingContact || isLoadingBuildings || isLoadingUnits) {
    return (
      <MuiPageWrapper>
        <Stack
          direction="column"
          spacing={2}
          sx={{ flex: '1 1 100%', alignItems: 'center', justifyContent: 'center' }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading contact...
          </Typography>
        </Stack>
      </MuiPageWrapper>
    );
  }

  if (contactError || !contact) {
    return (
      <MuiPageWrapper>
        <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
          <Typography variant="body2" color="error">
            Failed to load contact. Please try again.
          </Typography>
          <Button variant="outlined" onClick={handleBack}>
            Back to Contact
          </Button>
        </Stack>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Stack direction="column" spacing={3} sx={{ flex: '1 1 100%' }}>
        <DashboardHeader title="Edit Contact">
          <Button
            color="secondary"
            variant="outlined"
            size="medium"
            startIcon={<ArrowLeftIcon />}
            onClick={handleBack}
          >
            Back to Contact
          </Button>
        </DashboardHeader>

        <Typography variant="body3">
          {isCommunityContact 
            ? 'Edit contact information and details for this community.'
            : 'Edit contact information and details. Select one or more property locations to associate with this contact.'}
        </Typography>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={4}>
              {/* Property Location Association - only show for location contacts */}
              {!isCommunityContact && (
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Property Location Association
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select the property locations this contact is associated with. At least one
                    location is required.
                  </Typography>

                  <FormAutocomplete
                    name="location_ids"
                    label="Property Locations"
                    options={propertyLocationOptions}
                    control={control}
                    multiple
                    filterSelectedOptions
                    placeholder="Select property locations"
                    size="small"
                    sx={{ maxWidth: 600 }}
                    required
                  />
                </Stack>
              )}

              {/* Basic Information */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Basic Information
                </Typography>

                <FormTextField
                  label="Contact Name"
                  placeholder="Enter contact name"
                  required
                  control={control}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  {...register('name', {
                    required: 'Contact name is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormSelect
                  label="Role"
                  placeholder="Select contact role"
                  required
                  control={control}
                  options={contactRoleOptions}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  {...register('role', {
                    required: 'Contact role is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormTextField
                  label="Additional Details"
                  placeholder="Enter any additional information about this contact"
                  control={control}
                  error={!!errors.additional_details}
                  helperText={errors.additional_details?.message}
                  {...register('additional_details')}
                  multiline
                  rows={3}
                  size="small"
                  sx={{ maxWidth: 600 }}
                />
              </Stack>

              {/* Location Association - Only for Tenants */}
              {isTenantRole && (buildingOptions.length > 0 || unitOptions.length > 0) && (
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Location Association (Optional)
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Associate this tenant with specific buildings or units. This is optional for
                    tenant contacts.
                  </Typography>

                  {buildingOptions.length > 0 && (
                    <FormAutocomplete
                      name="building_ids"
                      label="Buildings (Optional)"
                      options={buildingOptions}
                      control={control}
                      multiple
                      filterSelectedOptions
                      placeholder="Select buildings"
                      size="small"
                      sx={{ maxWidth: 600 }}
                    />
                  )}

                  {unitOptions.length > 0 && (
                    <FormAutocomplete
                      name="unit_ids"
                      label="Units (Optional)"
                      options={unitOptions}
                      control={control}
                      multiple
                      filterSelectedOptions
                      placeholder="Select units"
                      size="small"
                      sx={{ maxWidth: 600 }}
                    />
                  )}
                </Stack>
              )}

              {/* Contact Details */}
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">Contact Details</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlusIcon />}
                    onClick={handleAddDetail}
                  >
                    Add Detail
                  </Button>
                </Stack>

                {detailFields.map((field, index) => (
                  <Stack
                    key={field.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <FormSelect
                      label="Type"
                      control={control}
                      options={contactDetailTypeOptions}
                      {...register(`details.${index}.type` as const)}
                      sx={{
                        minWidth: { xs: '100%', sm: 100 },
                        maxWidth: { xs: '100%', sm: 120 },
                      }}
                      size="small"
                    />

                    <FormTextField
                      label="Value"
                      placeholder={
                        watch(`details.${index}.type`) === 'email'
                          ? 'Enter email address'
                          : 'Enter phone number'
                      }
                      control={control}
                      error={!!errors.details?.[index]?.value}
                      helperText={errors.details?.[index]?.value?.message}
                      {...register(`details.${index}.value` as const, {
                        required: 'Value is required',
                        pattern:
                          watch(`details.${index}.type`) === 'email'
                            ? {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            }
                            : undefined,
                      })}
                      sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: 200 },
                      }}
                      size="small"
                    />

                    <FormSelect
                      label="Label"
                      control={control}
                      options={contactDetailLabelOptions}
                      {...register(`details.${index}.label` as const)}
                      sx={{
                        minWidth: { xs: '100%', sm: 120 },
                        maxWidth: { xs: '100%', sm: 150 },
                      }}
                      size="small"
                    />

                    <IconButton
                      onClick={() => removeDetail(index)}
                      disabled={detailFields.length <= 1}
                      color="error"
                      sx={{
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                        mt: { xs: 0, sm: 2 },
                      }}
                    >
                      <Trash01Icon />
                    </IconButton>
                  </Stack>
                ))}

                {detailFields.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No contact details added. Click "Add Detail" to add email or phone information.
                  </Typography>
                )}
              </Stack>

              {/* Form Error */}
              {formError && <FormHelperText error>{formError.message}</FormHelperText>}

              {/* Form Actions */}
              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={
                    isSubmitting || 
                    updateLocationContactMutation.isPending || 
                    updateCommunityContactMutation.isPending
                  }
                  disabled={
                    isSubmitting || 
                    updateLocationContactMutation.isPending || 
                    updateCommunityContactMutation.isPending
                  }
                >
                  Save Changes
                </LoadingButton>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={
                    isSubmitting || 
                    updateLocationContactMutation.isPending || 
                    updateCommunityContactMutation.isPending
                  }
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </MuiPageWrapper>
  );
};

export default ContactEditPage;
